import { employeeService } from "@/api/employees";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Gender, User } from "@/types";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { useEffect, useMemo, useState } from "react";

interface ProfileCompletionModalProps {
  user: User;
  isOpen: boolean;
  onCompleted: (updates: Partial<User>) => void;
}

interface ProfileFormData {
  contactNumber: string;
  address: string;
  dob: string;
  gender: string;
  emergencyContact: string;
}

function toDateInputValue(value?: string): string {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().split("T")[0];
}

export function ProfileCompletionModal({
  user,
  isOpen,
  onCompleted,
}: ProfileCompletionModalProps) {
  const { toast } = useToast();

  const initialData = useMemo<ProfileFormData>(
    () => ({
      contactNumber: user.contactNumber || "",
      address: user.address || "",
      dob: toDateInputValue(user.dob),
      gender: user.gender || Gender.NOT_SPECIFIED,
      emergencyContact: user.emergencyContact || "",
    }),
    [
      user.contactNumber,
      user.address,
      user.dob,
      user.gender,
      user.emergencyContact,
    ],
  );

  const [formData, setFormData] = useState<ProfileFormData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialData);
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await employeeService.update(user.id, {
        contactNumber: formData.contactNumber.trim(),
        address: formData.address.trim(),
        dob: formData.dob,
        gender: formData.gender as Gender,
        emergencyContact: formData.emergencyContact.trim(),
        isProfileCompleted: true,
      });

      onCompleted({
        contactNumber: formData.contactNumber.trim(),
        address: formData.address.trim(),
        dob: formData.dob,
        gender: formData.gender as Gender,
        emergencyContact: formData.emergencyContact.trim(),
        isProfileCompleted: true,
      });

      toast({
        title: "Profile updated",
        description: "Your profile has been completed successfully",
      });
    } catch (error: unknown) {
      toast({
        title: "Update failed",
        description: getErrorMessage(error, "Failed to update profile"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        // Intentionally non-dismissible. Parent keeps this open until completion.
      }}
    >
      <DialogContent
        className="sm:max-w-[560px]"
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Complete Your Profile</DialogTitle>
          <DialogDescription>
            Complete your personal details to continue using the application. This is a one-time update of your personal information, so please make sure everything is entered correctly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-email">Email</Label>
              <Input id="profile-email" value={user.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-role">Role</Label>
              <Input
                id="profile-role"
                value={user.roles?.[0]?.toUpperCase() || "EMPLOYEE"}
                disabled
                className="bg-muted"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactNumber">Phone</Label>
              <Input
                id="contactNumber"
                value={formData.contactNumber}
                onChange={(e) =>
                  setFormData({ ...formData, contactNumber: e.target.value })
                }
                placeholder="+919876543210"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => setFormData({ ...formData, gender: value })}
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={Gender.NOT_SPECIFIED}>Do Not Specify</SelectItem>
                  <SelectItem value={Gender.MALE}>Male</SelectItem>
                  <SelectItem value={Gender.FEMALE}>Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="emergencyContact">Emergency Contact</Label>
              <Input
                id="emergencyContact"
                value={formData.emergencyContact}
                onChange={(e) =>
                  setFormData({ ...formData, emergencyContact: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save and Continue"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
